import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * Back to a results page (collection or search): put the product that was
 * tapped back where it was on screen.
 *
 * The browser restores a scroll offset, but the page it comes back to is not the
 * page that was left. With infinite scroll the shopper had pages 1..n in the
 * document; Back rebuilds only the page the tapped product is on, so the same
 * offset points somewhere else (measured in the app: 308px off on a collection
 * page, off-screen on search results). assets/product-card.js records the tapped
 * card and its position at the moment of the tap; that is the one thing that is
 * still true after the reload.
 */
function restoreTappedCard() {
  // Scroll restoration is switched to 'manual' below for this view of the page
  // only. Left on the history entry, Safari puts the page at the top even when
  // it brings it back whole from its page cache (1,500px -> 0px on a plain
  // page; Chrome keeps the position). Hand it back when the page is left.
  const takeOver = () => {
    history.scrollRestoration = 'manual';
    window.addEventListener('pagehide', () => {
      history.scrollRestoration = 'auto';
    });
  };

  // A fresh visit to a copied URL still carries the anchor; let it do its job.
  const navigation = performance.getEntriesByType('navigation')[0];
  if (navigation instanceof PerformanceNavigationTiming && navigation.type === 'navigate') return;

  // Numbered pages (desktop): the page that comes back is the page that was
  // left, and the browser's own position is right. Only infinite scroll, whose
  // markers are hidden on desktop, leaves a page that cannot be rebuilt as it was.
  const marker = document.querySelector('results-list [ref="viewMoreNext"], results-list [ref="viewMorePrevious"]');
  if (!(marker instanceof HTMLElement) || getComputedStyle(marker).display === 'none') return;

  const saved = history.state?.includCard;
  if (!saved || typeof saved.top !== 'number') {
    // Back after leaving some other way (the logo, the bag, a tab of the app):
    // there is no product to return to. `?page=` in the URL means infinite
    // scroll had loaded more pages of results, and only that one page has been
    // rebuilt, so the browser's offset points at unrelated products or past the
    // end of the list (8,237px from where the shopper was, in a test). Do not
    // scroll: stay at the top of this page of results. Without `?page=` the
    // page is the one that was left and the browser's own offset is right.
    if (new URLSearchParams(window.location.search).has('page')) {
      takeOver();
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    return;
  }

  takeOver();

  const place = () => {
    const card = document.getElementById(saved.id);
    if (card) window.scrollBy({ top: Math.round(card.getBoundingClientRect().top) - saved.top, behavior: 'instant' });
    return Boolean(card);
  };

  // Infinite scroll puts the page before this one back above the card as soon
  // as the list is on screen, which moves the card by a page's height. Keep it
  // pinned until the shopper takes over.
  const list = document.querySelector('results-list');
  const pin = new MutationObserver(place);
  if (list) pin.observe(list, { childList: true, subtree: true });
  window.addEventListener('load', place);

  // `pointerdown` is for a mouse: a click on a filter, or a drag of the scrollbar.
  const inputs = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
  const release = () => {
    pin.disconnect();
    window.removeEventListener('load', place);
    for (const type of inputs) window.removeEventListener(type, release);
    // Forget the card, so a later Back to this page that did not start with a
    // product tap does not return to it. Restoration is not handed back here:
    // doing that while the page was still loading made the browser chase the
    // old offset (a touch 60ms after DOMContentLoaded landed 9,563px from the
    // card and loaded two extra pages on the way). It goes back on `pagehide`.
    history.replaceState({ ...history.state, includCard: null }, '');
  };
  for (const type of inputs) window.addEventListener(type, release, { passive: true });

  // The card is not on this page (the product moved to another page of results
  // or is no longer listed). The browser may already have applied its own
  // offset, which belongs to the longer page the shopper left and points at
  // unrelated products here. Start from the top of this page of results instead,
  // as a fresh visit to this URL would; the pin still moves to the card if it
  // arrives with the page before this one.
  if (!place()) window.scrollTo({ top: 0, behavior: 'instant' });
}

restoreTappedCard();

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
